import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from "@translations/config";
import { localizedHref } from "@/hooks/useLocalizedHref";

export const SITE_URL = "https://holymoly.dev";
export const SITE_NAME = "Holy Moly";

const OG_IMAGE_BY_LOCALE: Record<SupportedLocale, string> = {
  en: `${SITE_URL}/og-image.png`,
  fr: `${SITE_URL}/og-image-fr.png`,
};

const OG_LOCALE_TAG: Record<SupportedLocale, string> = {
  en: "en_US",
  fr: "fr_FR",
};

const HREFLANG_TAG: Record<SupportedLocale, string> = {
  en: "en",
  fr: "fr",
};

interface RouteSeoInput {
  /** Locale-agnostic path, e.g. "/work/equip". Leading slash required. */
  path: string;
  title: string;
  description: string;
  /** Active locale for this rendering. Defaults to English. */
  locale?: SupportedLocale;
  /**
   * Locales this route is published in. Defaults to all locales.
   * Pass a subset (e.g. `["en"]`) for English-only pages — they're omitted
   * from hreflang alternates.
   */
  availableLocales?: readonly SupportedLocale[];
  /** Absolute URL override. Defaults to the locale's OG image. */
  image?: string;
  /** Keep auth/private routes out of the index. */
  noindex?: boolean;
}

interface HeadTags {
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
}

/**
 * Build the meta + links for a route's `head` option.
 * Used in src/router.tsx to give each route its own title/description/OG.
 */
export function routeSeo(input: RouteSeoInput): HeadTags {
  const locale = input.locale ?? DEFAULT_LOCALE;
  const available = input.availableLocales ?? SUPPORTED_LOCALES;
  const canonicalUrl = `${SITE_URL}${localizedHref(input.path, locale)}`;
  const image = input.image ?? OG_IMAGE_BY_LOCALE[locale];
  const robots = input.noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1";

  const links: Array<Record<string, string>> = [{ rel: "canonical", href: canonicalUrl }];

  // hreflang alternates. Emit one per available locale, plus x-default
  // pointing at the English version (the universal fallback).
  if (!input.noindex) {
    for (const loc of available) {
      links.push({
        rel: "alternate",
        hrefLang: HREFLANG_TAG[loc],
        href: `${SITE_URL}${localizedHref(input.path, loc)}`,
      });
    }
    if (available.includes(DEFAULT_LOCALE)) {
      links.push({
        rel: "alternate",
        hrefLang: "x-default",
        href: `${SITE_URL}${localizedHref(input.path, DEFAULT_LOCALE)}`,
      });
    }
  }

  // og:locale:alternate — tell scrapers the page exists in the other locales.
  const ogLocaleAlternates = available
    .filter((loc) => loc !== locale)
    .map((loc) => ({
      property: "og:locale:alternate",
      content: OG_LOCALE_TAG[loc],
    }));

  return {
    meta: [
      { title: input.title },
      { name: "description", content: input.description },
      { name: "robots", content: robots },

      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:locale", content: OG_LOCALE_TAG[locale] },
      ...ogLocaleAlternates,
      { property: "og:title", content: input.title },
      { property: "og:description", content: input.description },
      { property: "og:url", content: canonicalUrl },
      { property: "og:image", content: image },

      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: input.title },
      { name: "twitter:description", content: input.description },
      { name: "twitter:image", content: image },
    ],
    links,
  };
}
